import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const key = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

/**
 * The app is fully usable with no backend at all — Dexie is what the UI reads
 * and writes. When these are absent the sync engine simply never runs, rather
 * than the app failing to start.
 */
export const isConfigured = Boolean(url && key);

export const supabase: SupabaseClient | null = isConfigured
  ? createClient(url!, key!, {
      auth: {
        // "One user, permanently signed in": the session is persisted and
        // refreshed silently, so sign-in happens once per device and never again.
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
      },
    })
  : null;

export async function currentUserId(): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.user.id ?? null;
}

/** Anything that is not a real failure to talk to the server — offline, mostly. */
export function isOffline(error: unknown): boolean {
  if (!navigator.onLine) return true;
  const message = error instanceof Error ? error.message : String(error);
  return /fetch|network|Failed to fetch|timeout/i.test(message);
}
